import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import useTenantBranding from '../hooks/useTenantBranding';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { KeyRound, Loader2, Lock, ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const { branding } = useTenantBranding();

  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 6) return toast.error('Password must be at least 6 characters');
    if (pwd !== confirm) return toast.error('Passwords do not match');
    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/reset-password`, { token, new_password: pwd });
      toast.success('Password updated! You can log in now.');
      navigate('/login');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed — request a new link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950 p-4">
      <Card className="w-full max-w-md" data-testid="reset-password-page">
        <CardHeader className="text-center">
          {branding?.logo_url ? (
            <img
              src={branding.logo_url}
              alt={branding.name}
              className="w-14 h-14 rounded-2xl object-cover border border-white/10 mx-auto mb-3 shadow-lg"
              data-testid="reset-brand-logo"
            />
          ) : (
            <div className="w-14 h-14 rounded-full bg-sky-500/15 border border-sky-500/30 mx-auto mb-3 flex items-center justify-center">
              <KeyRound className="w-7 h-7 text-sky-400" />
            </div>
          )}
          {branding && (
            <p className="text-sm text-sky-300 font-semibold mb-1">{branding.name}</p>
          )}
          <CardTitle>Reset your password</CardTitle>
          <CardDescription>Pick a new password (at least 6 characters).</CardDescription>
        </CardHeader>

        {!token ? (
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-rose-500/30 bg-rose-500/10 p-4 text-rose-200">
              <AlertCircle className="h-5 w-5 text-rose-400 mt-0.5 shrink-0" />
              <p className="text-sm">Missing or invalid reset token. Please request a new reset link.</p>
            </div>
            <Link to="/forgot-password">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-1" /> Request new link
              </Button>
            </Link>
          </CardContent>
        ) : (
          <form onSubmit={submit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-pwd">New password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="new-pwd"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    minLength={6}
                    required
                    disabled={loading}
                    data-testid="reset-pwd-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-pwd">Confirm password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm-pwd"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    minLength={6}
                    required
                    disabled={loading}
                    data-testid="reset-confirm-input"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full btn-gradient-primary" disabled={loading} data-testid="reset-submit">
                {loading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Updating...</> : 'Update password'}
              </Button>
            </CardContent>
            <CardFooter className="justify-center">
              <Link to="/login" className="text-xs text-muted-foreground hover:underline">
                <ArrowLeft className="h-3 w-3 inline mr-1" /> Back to login
              </Link>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}
